import React, { FC } from 'react';

import { CopyToClipboardProps } from './types';

/**
 * Handle the triggering event, writing to the clipboard, and calling our event handlers from props.
 * @param props.text The text string that should be copied to the clipboard
 * @param props.onCopy (Optional) A function to be called when the text has successfully copied. Will receive the text as an argument
 * @param props.children (Optional... Technically) A React Element to be rendered. The copy will trigger via `onClick` on the child element. If the child element already has an `onClick` value set, it will be called as part of the triggering. If no child element is provided, nothing will be rendered.
 * @param event The event that triggered the copy to clipboard
 */
export const handleClick = ({ text, onCopy, children }: CopyToClipboardProps, event: Event) => {
  const elem = React.Children.only(children);

  navigator.clipboard.writeText(text).then(
    // Success Handler
    () => {
      // Handle onCopy
      if (typeof onCopy === 'function') onCopy(text);

      // Handle children click handler
      if (elem && elem.props && typeof elem.props.onClick === 'function') {
        elem.props.onClick(event);
      }
    },
    // Failure Handler
    (reason) => {
      console.error('CopyToClipboard was unable to copy to the clipboard!', reason);
    },
  );
};

/**
 * Simple component to enable copying text to the clipboard via a click event. Any action that would trigger `onClick` will trigger this.
 * @param props.text The text string that should be copied to the clipboard
 * @param props.onCopy (Optional) A function to be called when the text has successfully copied. Will receive the text as an argument
 * @param props.children (Optional... Technically) A React Element to be rendered. The copy will trigger via `onClick` on the child element. If the child element already has an `onClick` value set, it will be called as part of the triggering. If no child element is provided, nothing will be rendered.
 * @returns JSXElement
 */
const CopyToClipboard: FC<CopyToClipboardProps> = (props) => {
  const { text, children } = props;
  let elem: React.ReactElement | undefined = undefined;
  let result: React.ReactElement | null = null;

  // If text wasn't provided, output an error to console
  if (!text || typeof text !== 'string') {
    console.error(
      'CopyToClipboard was not provided valid text to copy!',
      `The value provided was: '${text}'`,
    );
  }

  // Catch the error if no child or multiple children were provided
  try {
    elem = React.Children.only(children);
  } catch (e) {
    console.error('CopyToClipboard requires a single child element!', e);
  }

  // Clone the child element, replacing it's onClick handler
  if (elem)
    result = (
      <>{React.cloneElement(elem, { ...elem.props, onClick: (event: Event) => handleClick(props, event) })}</>
    );

  return result;
};

export default CopyToClipboard;
